<template>
  <!-- Header -->
  <header v-if="isAdmin" class="bg-gray-900 bg-opacity-80 p-4 border  border-gray-800 shadow-xl">
    <div class="flex justify-between items-center max-w-7xl mx-auto">
      <h1 class="text-xl font-semibold text-indigo-300">จัดการแก๊งค์</h1>
      <button
        @click="toggleModal = 'AddGangModal'"
        class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        aria-label="เพิ่มแก๊งค์ใหม่"
      >
        <i class="fas fa-plus-circle text-xs"></i> เพิ่มแก๊งค์ใหม่
      </button>
    </div>
  </header>

  <!-- Location Select -->
  <div class="bg-gray-900 bg-opacity-80 p-4 border  border-gray-800 shadow-xl">
    <div class="flex items-center gap-2 w-full">
      <select
        v-model="selectedLocation"
        class="w-full bg-gray-900 border border-gray-700 px-3 py-2 focus:border-indigo-500 outline-none text-white shadow-inner"
      >
        <option value="">เลือกสถานที่</option>
        <option v-for="loc in locations" :key="loc" :value="loc">{{ loc }}</option>
      </select>
    </div>
  </div>

  <div class="bg pb-24 mb-auto h-full overflow-auto bg-gray-800" id="style-1">
    <div class="max-w-full mx-auto space-y-5 text-white">
      <!-- Error -->
      <div v-if="error" class="rounded-xl border border-red-700 bg-red-900 bg-opacity-30 p-4 text-sm text-red-200">
        <div class="flex items-start gap-3">
          <i class="fa-solid fa-triangle-exclamation text-red-300 text-lg"></i>
          <div>
            <p class="font-medium">{{ errorMessage }}</p>
            <p v-if="indexLink" class="mt-1">
              <a :href="indexLink" target="_blank" class="underline hover:no-underline">สร้างดัชนี (index)</a>
            </p>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-sm text-gray-300">กำลังโหลดข้อมูล…</div>

      <!-- Empty or No Location -->
      <div v-else-if="!selectedLocation || ganks.length === 0" class="border-gray-700 bg-gray-900 bg-opacity-80 p-8 text-center">
        <i class="fas fa-users-slash text-gray-400 text-3xl mb-2"></i>
        <p v-if="!selectedLocation">กรุณาเลือกสถานที่เพื่อแสดงรายการแก๊งค์</p>
        <p v-else>ยังไม่มีแก๊งค์ในพื้นที่นี้</p>
      </div>

      <table v-else class="min-w-full text-sm text-gray-200">
        <thead class="bg-gray-900 bg-opacity-90 text-gray-300">
          <tr>
            <th class="px-4 py-3 text-center w-40">-</th>
            <th class="px-4 py-3 text-center">แก๊งค์</th>
            <th class="px-4 py-3 text-center">ระดับ</th>
            <th class="px-4 py-3 text-center">จำนวนสมาชิก</th>
            <th class="px-4 py-3 text-center">ผ่านหรือยัง?</th>
            <th class="px-3 py-3 text-center w-10"></th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-800 bg-gray-800 bg-opacity-80 text-center">
          <tr
            v-for="row in ganks"
            :key="row.id"
            class="hover:bg-gray-700 hover:bg-opacity-50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500"
            @click="openDetailsModal(row.id)"
            tabindex="0"
            @keyup.enter="openDetailsModal(row.id)"
          >
            <td class="px-4 py-3">
              <img
                  v-if="row.img"
                  :src="row.img"
                  class="block w-40  rounded"
                  alt="Gang Image"
                  @error="handleImageError"
                />
            </td>
            <td class="px-4 py-3">
              <div class="text-center">
                
                {{ row.name }}
              </div>
            </td>
            <td class="px-4 py-3 font-semibold text-indigo-300">{{ row.level ?? 0 }}</td>
            <td class="px-4 py-3">{{ row.members ? row.members.length : 0 }}</td>
            <td class="px-4 py-3">
              <i v-if="wonGangs.includes(row.id)" class="fas fa-check text-green-500"></i>
              <i v-else class="fas fa-times text-red-500"></i>
            </td>
            <td class="px-3 py-3 text-right">
              <button
                v-if="isAdmin"
                class="p-2 rounded-lg hover:bg-gray-800 border border-transparent hover:border-gray-700 transition"
                aria-label="แก้ไขแก๊งค์"
                @click.stop="openEditModal(row.id)"
              >
                <i class="fa-solid fa-pen-to-square text-gray-300 text-sm"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Modals -->
      <AddGangModal
        v-if="toggleModal === 'AddGangModal'"
        @cancelModal="toggleModal = ''"
        @gangAdded="fetchData"
      />
      <EditGangModal
        v-if="editOpen && isAdmin"
        :gang-id="activeGangId"
        @cancelModal="closeEdit"
        @gangUpdated="fetchData"
      />
      <DetailGangModal
        v-if="detailOpen"
        :gang-id="activeGangId"
        @cancelModal="closeDetails"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount, computed } from 'vue';
import AddGangModal from '../components/admin/AddGangModal.vue';
import EditGangModal from '../components/admin/EditGangModal.vue';
import DetailGangModal from '../components/admin/DetailGangModal.vue';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  components: { AddGangModal, EditGangModal, DetailGangModal },
  setup() {
    const LOCAL_KEY = 'selectedGangLocation';

    const locations = [
      'รถไฟฟ้าใต้ดิน',
      'โรงพยาบาล', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า',
      'สถานีดับเพลิงร้าง', 'ค่ายทหาร',
      'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ',
      'โรงเก็บศพจักรกล', 'ตึกปฏิบัติการพันธุกรรม'
    ];

    // โหลดค่าจาก localStorage ถ้ามี
    const stored = localStorage.getItem(LOCAL_KEY);
    const selectedLocation = ref(stored && locations.includes(stored) ? stored : '');

    watch(selectedLocation, (val) => {
      localStorage.setItem(LOCAL_KEY, val);
      fetchData();
    });

    const loading = ref(false);
    const error = ref(null);
    const indexLink = ref('');
    const ganks = ref([]);
    let unsubscribe = null;

    const toggleModal = ref('');

    const fetchData = () => {
      if (!selectedLocation.value) {
        ganks.value = [];
        return;
      }
      loading.value = true;
      error.value = null;
      indexLink.value = '';
      ganks.value = [];

      if (unsubscribe) unsubscribe();

      const q = projectFirestore
        .collection('gameGanks')
        .where('location', '==', selectedLocation.value)
        .orderBy('level', 'asc')
        .orderBy('name', 'asc');

      unsubscribe = q.onSnapshot(
        snap => {
          ganks.value = snap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          loading.value = false;
        },
        err => {
          loading.value = false;
          error.value = err;
          const match = String(err.message).match(/https?:\/\/[^\s)]+/g);
          if (match) indexLink.value = match[0];
        }
      );
    };

    onMounted(fetchData);
    onBeforeUnmount(() => unsubscribe && unsubscribe());

    const errorMessage = computed(() =>
      error.value?.code?.includes('failed-precondition')
        ? 'ต้องสร้างดัชนี (index) ก่อนใช้งานคิวรีนี้'
        : error.value?.message || ''
    );

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = 'default-gang.png';
    };

    // ===== ตรวจสิทธิ์แอดมิน และ wonGangs ด้วย usePlayerStore =====
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const isAdmin = ref(false);
    const wonGangs = ref([]);

    watch(() => playerStore.userData, (data) => {
      isAdmin.value = data?.admin === true;
    }, { immediate: true });

    watch(() => playerStore.characterPrivateData, (data) => {
      wonGangs.value = data?.wonGangs || [];
    }, { immediate: true });

    // ===== Modal control: Detail =====
    const detailOpen = ref(false);
    const activeGangId = ref(null);
    const openDetailsModal = (id) => {
      activeGangId.value = id;
      detailOpen.value = true;
    };
    const closeDetails = () => {
      detailOpen.value = false;
      activeGangId.value = null;
    };

    // ===== Modal control: Edit (admin) =====
    const editOpen = ref(false);
    const openEditModal = (id) => {
      activeGangId.value = id;
      editOpen.value = true;
    };
    const closeEdit = () => {
      editOpen.value = false;
      activeGangId.value = null;
    };

    // Open edit from detail
    const openEditFromDetail = (gangId) => {
      activeGangId.value = gangId;
      editOpen.value = true;
      detailOpen.value = false;
    };

    return {
      locations,
      selectedLocation,
      loading,
      error,
      errorMessage,
      indexLink,
      ganks,
      wonGangs,
      // admin
      isAdmin,
      // user
      user,
      // modals
      toggleModal,
      detailOpen,
      activeGangId,
      openDetailsModal,
      closeDetails,
      editOpen,
      openEditModal,
      closeEdit,
      openEditFromDetail,
      handleImageError,
      fetchData
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
table th, table td { white-space: nowrap; }
</style>